/**
 * Saví Sava — Quick View modal interactions.
 *
 * This file is loaded globally (via snippets/quick-add-modal.liquid), because
 * the ".qv" markup it controls does not exist in the DOM until a shopper
 * triggers Horizon's native quick-add flow somewhere on the site (product
 * card, cart-drawer recommendation, etc.) — assets/quick-add.js fetches that
 * product's own page, clones the [data-product-grid-content] element
 * (rendered by sections/pdp-redesign.liquid) and morphs it into the global
 * quick-add dialog. Because that can happen long after this script first
 * runs, every listener here is delegated on `document` rather than bound to
 * specific elements.
 *
 * Real add-to-cart is handled by Horizon's own <product-form-component>
 * (on:submit="/handleSubmit"), the exact same mechanism sections/buy-buttons
 * uses, so it already posts to the cart, refreshes the cart drawer, and
 * (via quick-add.js's QuickAddDialog) closes the modal on success — nothing
 * extra to wire up here for that part. This file only owns: the image
 * gallery (arrows/thumbs/counter), color + size selection updating the
 * hidden variant input and the price/stock/save-badge display, and the
 * inline size-guide panel toggle.
 */
(function () {
  function moneyFromCents(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (format) return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    return '$' + amount;
  }

  function getVariants(root) {
    var script = root.querySelector('[data-qv-variants]');
    try {
      return JSON.parse((script && script.textContent) || '[]');
    } catch (e) {
      return [];
    }
  }

  function currentSelection(root) {
    var selection = {};
    var activeColor = root.querySelector('[data-qv-color-row] .qv__color-circle.is-active');
    if (activeColor) selection.color = activeColor.getAttribute('data-qv-color-value');
    var activeSize = root.querySelector('[data-qv-size-row] .qv__size-button.is-active');
    if (activeSize) selection.size = activeSize.getAttribute('data-qv-size-value');
    return selection;
  }

  function findVariant(variants, colorIndex, sizeIndex, selection) {
    return variants.filter(function (variant) {
      var options = variant.options || [];
      if (colorIndex > -1 && selection.color != null && options[colorIndex] !== selection.color) return false;
      if (sizeIndex > -1 && selection.size != null && options[sizeIndex] !== selection.size) return false;
      return true;
    })[0];
  }

  function refreshSizeAvailability(root, variants, colorIndex, sizeIndex) {
    var sizeRow = root.querySelector('[data-qv-size-row]');
    if (!sizeRow) return;
    var selection = currentSelection(root);
    sizeRow.querySelectorAll('[data-qv-size-value]').forEach(function (button) {
      var sizeValue = button.getAttribute('data-qv-size-value');
      var match = variants.filter(function (variant) {
        var options = variant.options || [];
        if (colorIndex > -1 && selection.color != null && options[colorIndex] !== selection.color) return false;
        if (sizeIndex > -1 && options[sizeIndex] !== sizeValue) return false;
        return true;
      })[0];
      var available = !!(match && match.available);
      if (!match || !available) {
        button.classList.add('is-unavailable');
      } else {
        button.classList.remove('is-unavailable');
      }
    });
  }

  function updateForVariant(root, qv, variant, lowStockThreshold) {
    if (!variant) return;

    var variantInput = root.querySelector('[data-qv-variant-input]');
    if (variantInput) variantInput.value = variant.id;

    var moneyFormat = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{ amount }}';
    var priceText = moneyFromCents(variant.price, moneyFormat);

    // Price/save-badge live in .qv__details, as siblings of <product-form-component>,
    // not inside it -- look them up from the wider .qv root.
    var priceCurrent = qv.querySelector('[data-qv-price]');
    if (priceCurrent) priceCurrent.textContent = priceText;

    var saveBadge = qv.querySelector('[data-qv-save-badge]');
    if (saveBadge) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        saveBadge.textContent = 'SAVE ' + pct + '%';
        saveBadge.style.display = '';
      } else {
        saveBadge.style.display = 'none';
      }
    }

    var addButton = root.querySelector('[data-qv-add-to-cart]');
    var addLabel = root.querySelector('[data-qv-add-label]');
    if (addButton) {
      addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'ADD TO BAG' : 'SOLD OUT';
    }

    var stockBox = root.querySelector('[data-qv-stock]');
    if (stockBox) {
      if (variant.inventory_management === 'shopify') {
        if (variant.inventory_quantity > 0 && variant.inventory_quantity <= lowStockThreshold) {
          stockBox.innerHTML =
            '<span class="qv__stock-dot qv__stock-dot--low"></span><span>Low stock — only ' +
            variant.inventory_quantity +
            ' left</span>';
          stockBox.style.display = '';
        } else if (variant.inventory_quantity > lowStockThreshold) {
          stockBox.innerHTML = '<span class="qv__stock-dot qv__stock-dot--in"></span><span>In stock</span>';
          stockBox.style.display = '';
        } else {
          stockBox.style.display = 'none';
        }
      }
    }
  }

  function handleColorClick(button) {
    var qv = button.closest('.qv');
    var form = button.closest('product-form-component');
    if (!qv || !form) return;

    var colorRow = button.closest('[data-qv-color-row]');
    colorRow.querySelectorAll('.qv__color-circle').forEach(function (el) {
      el.classList.remove('is-active');
      el.setAttribute('aria-pressed', 'false');
    });
    button.classList.add('is-active');
    button.setAttribute('aria-pressed', 'true');

    var colorNameLabel = qv.querySelector('[data-qv-color-name]');
    if (colorNameLabel) colorNameLabel.textContent = (button.getAttribute('data-qv-color-value') || '').toUpperCase();

    var colorIndex = parseInt(form.getAttribute('data-color-option-index'), 10);
    var sizeIndex = parseInt(form.getAttribute('data-size-option-index'), 10);
    var lowStockThreshold = parseInt(form.getAttribute('data-low-stock-threshold'), 10) || 10;
    var variants = getVariants(qv);

    refreshSizeAvailability(form, variants, colorIndex, sizeIndex);
    var variant = findVariant(variants, colorIndex, sizeIndex, currentSelection(form));
    if (variant) updateForVariant(form, qv, variant, lowStockThreshold);
  }

  function handleSizeClick(button) {
    if (button.classList.contains('is-unavailable')) return;
    var qv = button.closest('.qv');
    var form = button.closest('product-form-component');
    if (!qv || !form) return;

    var sizeRow = button.closest('[data-qv-size-row]');
    sizeRow.querySelectorAll('.qv__size-button').forEach(function (el) {
      el.classList.remove('is-active');
    });
    button.classList.add('is-active');

    var colorIndex = parseInt(form.getAttribute('data-color-option-index'), 10);
    var sizeIndex = parseInt(form.getAttribute('data-size-option-index'), 10);
    var lowStockThreshold = parseInt(form.getAttribute('data-low-stock-threshold'), 10) || 10;
    var variants = getVariants(qv);

    var variant = findVariant(variants, colorIndex, sizeIndex, currentSelection(form));
    if (variant) updateForVariant(form, qv, variant, lowStockThreshold);
  }

  function setActiveSlide(qv, index) {
    var slides = qv.querySelectorAll('.qv__slide');
    var thumbs = qv.querySelectorAll('[data-qv-thumb]');
    var counter = qv.querySelector('[data-qv-counter]');
    var total = slides.length;
    if (!total) return;
    var normalized = ((index % total) + total) % total;

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === normalized);
    });
    thumbs.forEach(function (thumb) {
      thumb.classList.toggle('is-active', parseInt(thumb.getAttribute('data-index'), 10) === normalized);
    });
    if (counter) counter.textContent = normalized + 1 + ' / ' + total;
    qv.setAttribute('data-qv-active-index', String(normalized));
  }

  document.addEventListener('click', function (event) {
    var target = event.target;

    var thumb = target.closest && target.closest('[data-qv-thumb]');
    if (thumb) {
      var qv = thumb.closest('.qv');
      if (qv) setActiveSlide(qv, parseInt(thumb.getAttribute('data-index'), 10));
      return;
    }

    var prev = target.closest && target.closest('[data-qv-prev]');
    if (prev) {
      var qvPrev = prev.closest('.qv');
      if (qvPrev) {
        var current = parseInt(qvPrev.getAttribute('data-qv-active-index'), 10) || 0;
        setActiveSlide(qvPrev, current - 1);
      }
      return;
    }

    var next = target.closest && target.closest('[data-qv-next]');
    if (next) {
      var qvNext = next.closest('.qv');
      if (qvNext) {
        var currentNext = parseInt(qvNext.getAttribute('data-qv-active-index'), 10) || 0;
        setActiveSlide(qvNext, currentNext + 1);
      }
      return;
    }

    var colorCircle = target.closest && target.closest('[data-qv-color-value]');
    if (colorCircle) {
      handleColorClick(colorCircle);
      return;
    }

    var sizeButton = target.closest && target.closest('[data-qv-size-value]');
    if (sizeButton) {
      handleSizeClick(sizeButton);
      return;
    }

    var sizeGuideToggle = target.closest && target.closest('[data-qv-size-guide-toggle]');
    if (sizeGuideToggle) {
      var qvRoot = sizeGuideToggle.closest('.qv');
      var panel = qvRoot && qvRoot.querySelector('[data-qv-size-guide-panel]');
      if (panel) panel.hidden = !panel.hidden;
      return;
    }
  });
})();
